import { MdClose } from 'react-icons/md';
import { useState, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Sidebar, type SidebarProps } from './sidebar';
import { Topbar, type TopbarProps } from './topbar';

export type AppShellProps = Pick<
  SidebarProps,
  'groups' | 'footerGroups' | 'renderLink' | 'logoBadge'
> & {
  /** Encart libre en bas de la navigation. */
  sidebarFooter?: ReactNode;
  user?: TopbarProps['user'];
  userMenuItems?: TopbarProps['userMenuItems'];
  /** Zone libre à droite de la topbar (badge d'abonnement, notifications…). */
  /** Zone libre à gauche de la topbar (cabinet courant, fil d'Ariane…). */
  topbarStart?: ReactNode;
  topbarEnd?: ReactNode;
  /**
   * Bandeau pleine largeur sous la barre du haut, pour un état qui concerne
   * toute l'application — un impayé, une coupure imminente. Hors du `main` :
   * il ne doit pas défiler avec le contenu ni hériter de ses marges.
   */
  banner?: ReactNode;
  children: ReactNode;
  /**
   * Classe du `<main>`, pour les écrans qui vont jusqu'aux bords.
   *
   * La marge de contenu est la règle — un écran pose ses cartes sur le fond
   * crème — mais elle ne vaut pas pour tous : une boîte de réception doit
   * toucher les bords de sa zone, sinon elle flotte comme un widget au lieu de
   * se lire comme une application. `p-0` suffit alors, la classe passée ici
   * l'emportant sur celle par défaut.
   */
  mainClassName?: string;
  /**
   * Borne la coquille à la hauteur de la fenêtre.
   *
   * Par défaut elle fait `min-h-dvh` : elle remplit l'écran et s'allonge avec
   * son contenu, c'est la page qui défile. Certains écrans veulent l'inverse —
   * une boîte de réception, un tableau à deux panneaux — où le cadre reste
   * fixe et où chaque panneau défile à l'intérieur. `flex-1` ne suffit pas à
   * l'obtenir : sans hauteur plafonnée en haut de la chaîne, un contenu plus
   * grand que l'écran repousse simplement le bas de la page.
   */
  fullHeight?: boolean;
  className?: string;
};

/**
 * Coquille applicative : navigation latérale + barre supérieure + contenu.
 * Sous `lg`, la navigation passe en drawer.
 */
export function AppShell({
  groups,
  footerGroups,
  renderLink,
  logoBadge,
  sidebarFooter,
  user,
  userMenuItems,
  topbarStart,
  topbarEnd,
  banner,
  children,
  mainClassName,
  fullHeight = false,
  className,
}: AppShellProps) {
  const [navOpen, setNavOpen] = useState(false);

  // Avec un bandeau, la fenêtre ne peut plus donner toute sa hauteur à la colonne
  // de navigation (bandeau + colonne dépasseraient l'écran, et la page défilerait
  // avec une navigation coupée). La coquille prend alors la hauteur de l'écran et
  // c'est le contenu qui défile, sous une barre du haut qui reste en place.
  const contrainte = fullHeight || Boolean(banner);
  const defileDedans = Boolean(banner) && !fullHeight;

  const sidebar = (
    <Sidebar
      groups={groups}
      footerGroups={footerGroups}
      renderLink={renderLink}
      footer={sidebarFooter}
      logoBadge={logoBadge}
    />
  );

  return (
    <div
      className={cn(
        'flex flex-col bg-surface-page',
        // `overflow-hidden` avec la hauteur fixe : sans lui, un panneau qui
        // déborde ferait réapparaître l'ascenseur de la fenêtre par-dessus
        // celui du panneau, et on se retrouverait avec les deux.
        contrainte ? 'h-dvh overflow-hidden' : 'min-h-dvh',
        className,
      )}
    >
      {/* Au-dessus de la navigation, pas seulement du contenu : un état qui
          concerne toute l'application doit traverser toute la largeur. */}
      {banner}

      <div className="flex min-h-0 flex-1">
        {/* `sticky` plutôt que `fixed` : la colonne reste dans le flux, donc le
            contenu n'a pas à compenser sa largeur par une marge. Elle occupe la
            hauteur de la fenêtre et ne défile plus avec la page. */}
        <div
          className={cn(
            'hidden lg:flex lg:shrink-0',
            contrainte ? 'lg:h-full' : 'lg:sticky lg:top-0 lg:h-dvh',
          )}
        >
          {sidebar}
        </div>

        {navOpen ? (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              aria-label="Fermer la navigation"
              onClick={() => setNavOpen(false)}
              className="absolute inset-0 bg-brand-900/50"
            />
            <div className="relative flex h-full" onClick={() => setNavOpen(false)}>
              {sidebar}
              <button
                type="button"
                aria-label="Fermer la navigation"
                onClick={() => setNavOpen(false)}
                className="m-4 h-10 rounded-lg px-3 text-white/80 transition-colors hover:bg-white/10"
              >
                <MdClose className="size-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}

        <div
          // Repère pour le routeur : quand le contenu défile ici et non dans la fenêtre,
          // c'est cet élément qu'il remet en haut au changement de page.
          data-scroll-root
          className={cn('flex min-w-0 flex-1 flex-col', defileDedans && 'overflow-y-auto')}
        >
          <Topbar
            // Toujours collée en haut : la page défile (cas par défaut) ou le contenu
            // défile dans la coquille, la barre ne bouge pas.
            className="sticky top-0 z-20"
            user={user}
            userMenuItems={userMenuItems}
            start={topbarStart}
            end={topbarEnd}
            onOpenNav={() => setNavOpen(true)}
          />
          {/*
            `flex flex-col` + `min-h-0` : un écran qui veut tenir dans la
            fenêtre — réglages, tableaux à défilement interne — pose un
            `min-h-0 flex-1` sur sa racine et hérite ici d'une hauteur définie
            au lieu de pousser la page. Les autres pages empilent leur contenu
            comme avant.
          */}
          <main
            className={cn(
              'flex flex-1 flex-col p-4 sm:p-6',
              // Le contenu défile dans la coquille (`defileDedans`) : `main` doit alors
              // grandir avec lui. Avec `min-h-0` il se réduisait à la hauteur de l'écran,
              // le contenu débordait et le padding du bas restait hors de portée du
              // défilement — la page finissait collée au bord de la fenêtre.
              !defileDedans && 'min-h-0',
              mainClassName,
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
